import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Team, TeamMember } from '@/types/api';

interface TeamState {
  currentTeam: Team | null;
  members: TeamMember[];
  isLoading: boolean;
  error: string | null;
}

interface TeamActions {
  setCurrentTeam: (team: Team | null) => void;
  setMembers: (members: TeamMember[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  updateTeam: (updates: Partial<Team>) => void;
  reset: () => void;
}

type TeamStore = TeamState & TeamActions;

const initialState: TeamState = {
  currentTeam: null,
  members: [],
  isLoading: false,
  error: null,
};

export const useTeamStore = create<TeamStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      setCurrentTeam: (currentTeam) => set({ currentTeam }),

      setMembers: (members) => set({ members }),

      setLoading: (isLoading) => set({ isLoading }),

      setError: (error) => set({ error }),

      updateTeam: (updates) => {
        const { currentTeam } = get();
        if (currentTeam) {
          set({ currentTeam: { ...currentTeam, ...updates } });
        }
      },

      reset: () => set(initialState),
    }),
    {
      name: 'tc-insights-team',
      partialize: (state) => ({
        // Persist team selection across sessions
        currentTeam: state.currentTeam
          ? { id: state.currentTeam.id, name: state.currentTeam.name }
          : null,
      }),
    }
  )
);

// Selector hooks for common patterns
export const useCurrentTeam = () => useTeamStore((state) => state.currentTeam);
export const useTeamId = () => useTeamStore((state) => state.currentTeam?.id);
